import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { ar } from "date-fns/locale";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Feather, Lock, Trash2, Send } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];

export default function Posts() {
  const { user } = useAuth();
  const [posts, setPosts] = useState(null);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);

  const canPublish = user.is_active && (user.role === "admin" || user.can_post);

  useEffect(() => {
    api("/posts").then(setPosts).catch((e) => toast.error(e.message));
  }, []);

  const submit = async () => {
    if (!body.trim()) return;
    setSending(true);
    try {
      const post = await api("/posts", { method: "POST", body: JSON.stringify({ body: body.trim() }) });
      setPosts((p) => [post, ...(p || [])]);
      setBody("");
      toast.success("تم نشر المنشور");
    } catch (e) {
      toast.error(e.message);
    } finally {
      setSending(false);
    }
  };

  const remove = async (postId) => {
    try {
      await api(`/posts/${postId}`, { method: "DELETE" });
      setPosts((p) => p.filter((x) => x.post_id !== postId));
      toast.success("تم حذف المنشور");
    } catch (e) {
      toast.error(e.message);
    }
  };

  return (
    <div data-testid="posts-page">
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: EASE }}>
        <h1 className="mb-2 flex items-center gap-3 font-amiri text-4xl text-navy sm:text-5xl">
          <Feather className="h-8 w-8 text-gold-dark" />
          المنشورات
        </h1>
        <p className="mb-8 text-muted-foreground">بيانات الاتحاد وأخباره — نصوص فقط، بأقلام المفوَّضين.</p>
      </motion.div>

      {canPublish ? (
        <motion.div
          className="mb-10 rounded-2xl border border-gold/30 bg-white p-6 shadow-sm"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1, ease: EASE }}
          data-testid="post-composer"
        >
          <Textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="اكتب منشوراً نصياً للاتحاد..."
            data-testid="post-composer-textarea"
            className="min-h-[120px] resize-y border-input bg-slate-50 text-navy placeholder:text-muted-foreground focus-visible:ring-gold"
            maxLength={2000}
          />
          <div className="mt-4 flex items-center justify-between">
            <span className="text-xs text-muted-foreground">{body.length}/2000</span>
            <Button
              onClick={submit}
              disabled={sending || !body.trim()}
              data-testid="post-submit-button"
              className="rounded-full bg-gold px-6 font-bold text-navy transition-[transform,background-color] duration-300 hover:-translate-y-0.5 hover:bg-gold-light disabled:opacity-50"
            >
              <Send className="ml-2 h-4 w-4" />
              {sending ? "جارٍ النشر..." : "نشر"}
            </Button>
          </div>
        </motion.div>
      ) : (
        <motion.div
          className="mb-10 flex items-center gap-4 rounded-2xl border border-gold/30 bg-[#FAF8F3] p-6"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1, ease: EASE }}
          data-testid="post-permission-notice"
        >
          <Lock className="h-6 w-6 shrink-0 text-gold-dark" />
          <p className="text-sm leading-7 text-muted-foreground">
            {!user.is_active
              ? "حسابك بانتظار التفعيل من الإدارة. يمكنك تصفّح المنشورات، لكن النشر والتصويت يتاحان بعد التفعيل."
              : "النشر متاح فقط للأعضاء المفوَّضين من الإدارة. يمكنك قراءة المنشورات بحرية."}
          </p>
        </motion.div>
      )}

      <div className="space-y-5" data-testid="posts-list">
        {posts === null ? (
          <p className="py-16 text-center text-muted-foreground">جارٍ التحميل...</p>
        ) : posts.length === 0 ? (
          <motion.div
            className="rounded-2xl border border-dashed border-gold/40 py-20 text-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
          >
            <Feather className="mx-auto mb-4 h-10 w-10 text-gold/50" />
            <p className="font-amiri text-2xl text-muted-foreground">لا توجد منشورات بعد</p>
          </motion.div>
        ) : (
          posts.map((post, i) => (
            <motion.article
              key={post.post_id}
              data-testid={`post-card-${post.post_id}`}
              className="group rounded-2xl border border-gold/25 bg-white p-6 shadow-sm transition-[border-color,box-shadow] duration-300 hover:border-gold/50 hover:shadow-[0_12px_36px_rgba(10,30,60,0.08)]"
              initial={{ opacity: 0, y: 32 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, delay: Math.min(i * 0.07, 0.5), ease: EASE }}
            >
              <div className="mb-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {post.author_picture ? (
                    <img src={post.author_picture} alt={post.author_name} className="h-10 w-10 rounded-full border border-gold/40" referrerPolicy="no-referrer" />
                  ) : (
                    <span className="flex h-10 w-10 items-center justify-center rounded-full bg-navy font-amiri text-lg text-gold">
                      {post.author_name?.[0] || "ن"}
                    </span>
                  )}
                  <div>
                    <p className="font-bold text-navy">{post.author_name}</p>
                    <p className="text-xs text-muted-foreground">
                      {format(new Date(post.created_at), "d MMMM yyyy — h:mm a", { locale: ar })}
                    </p>
                  </div>
                </div>
                {user.role === "admin" && (
                  <button
                    onClick={() => remove(post.post_id)}
                    data-testid={`post-delete-${post.post_id}`}
                    className="rounded-full p-2 text-muted-foreground opacity-0 transition-[color,background-color,opacity] duration-300 hover:bg-red-50 hover:text-red-500 group-hover:opacity-100"
                    title="حذف المنشور"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
              <p className="whitespace-pre-wrap leading-8 text-navy/90">{post.body}</p>
            </motion.article>
          ))
        )}
      </div>
    </div>
  );
}
